/*
	Body markup for the Diagram and RichText editor input types, assembled per presentation by the
	{~EditorBody:<Theme>~} macro (see HeadlightDynamicInputsBundle.js). Both types share this markup —
	they differ only in the display slot's class and their provider hash, both derived from the InputType.

	The field edits INLINE: a View/Edit segmented switch flips between the static view and the live
	editor in place (the provider only toggles `is-editing`; the CSS does the rest). The inactive segment
	is a soft link, the active one solid — they read as one control. Read and Print always render the
	static view (no switch, no edit affordance); Print additionally drops the view's border (see the
	`pict-editor-print` class) so the PDF is clean.

	Every editable field opens and closes through that switch, which is what makes the exported value
	reliable: the close path commits. There is deliberately no "always editing" presentation (a field
	with no close event has no natural point to export from) and no floating "compact" modal one (removed
	2026-07-22 — the inline editor has its own toolbar, so the modal was bug surface without a need). There
	is also no dedicated "Clear" control (added then removed 2026-07-22): Excalidraw's own nav menu carries
	a "reset the canvas" action, which is deliberate-and-buried where a toolbar button was easy to mis-hit.

	Two ids are load-bearing and must not change:
	  - id="DISPLAY-FOR-{RawHTMLID}" — the PSF provider mounts view/edit content there.
	  - id="PICT-EDITOR-FOR-{RawHTMLID}" — the provider toggles `is-editing` on this container.
*/

/** @param {string} pInputType @return {string} The PSF display-slot class for this editor type. */
const displayClass = (pInputType) => `pict-section-form-${pInputType.toLowerCase()}`;

/** The View | Edit segmented switch. Each segment names its target mode (not a flip), so it reads as an
 *  on-page mode switch. Both segments are `is-link is-soft`; CSS promotes the ACTIVE one to solid link
 *  (keyed off the container's `is-editing`), so the pair reads as soft-inactive / solid-active. */
const _modeSwitch = (pInputType) => /*HTML*/`
			<div class="buttons has-addons pict-editor-switch">
				<button type="button" class="button is-small is-link is-soft pict-editor-switch-view" {~Disabled:Record~}
					onclick="_Pict.providers['Pict-Input-${pInputType}'].setEditorMode('{~D:Record.Hash~}', '{~D:Record.Macro.RawHTMLID~}', 'view')">
					<span class="icon"><i class="fas fa-fw fa-eye"></i></span><span>View</span>
				</button>
				<button type="button" class="button is-small is-link is-soft pict-editor-switch-edit" {~Disabled:Record~}
					onclick="_Pict.providers['Pict-Input-${pInputType}'].setEditorMode('{~D:Record.Hash~}', '{~D:Record.Macro.RawHTMLID~}', 'edit')">
					<span class="icon"><i class="fas fa-fw fa-pen"></i></span><span>Edit</span>
				</button>
			</div>`;

/**
 * Build the body markup for one editor presentation.
 *
 * @param {object} pOptions
 * @param {string} pOptions.Theme - 'Edit' | 'Read' | 'Print'.
 * @param {string} pOptions.InputType - 'Diagram' | 'RichText'.
 * @return {string} The (unparsed) body template.
 */
function buildEditorBody(pOptions)
{
	const tmpInputType = pOptions.InputType;
	// Read/Print are always the static view — the editor chrome means nothing there.
	const tmpLocked = (pOptions.Theme !== 'Edit');
	// Print drops the view border for a clean PDF; Read keeps it (an on-screen affordance).
	const tmpContainerClass = (pOptions.Theme === 'Print') ? 'pict-editor pict-editor-print' : 'pict-editor';

	// The change handler lets informary marshal the exported value into FormData; drop it in the
	// read-only theme where nothing changes (matching the HTML/Markdown templates).
	const tmpChangeHandler = tmpLocked ? '' : '{~D:Record.Macro.InputChangeHandler~}';

	// The explicit id is REQUIRED, not decoration: the provider writes the exported value back with
	// document.getElementById(RawHTMLID) and returns silently when it finds nothing — so without it the
	// editor works, shows content, and saves absolutely nothing. InputFullProperties supplies the
	// informary binding (data-i-form / data-i-datum / name) but NOT the id.
	return /*HTML*/`
		<input type="hidden" tabindex="-1" id="{~D:Record.Macro.RawHTMLID~}" {~D:Record.Macro.InputFullProperties~} ${tmpChangeHandler} value="">
		<div id="PICT-EDITOR-FOR-{~D:Record.Macro.RawHTMLID~}" class="${tmpContainerClass}">
			${tmpLocked ? '' : /*HTML*/`<div class="pict-editor-controls">${_modeSwitch(tmpInputType)}</div>`}
			<div {~D:Record.Macro.ControlAttr~} id="DISPLAY-FOR-{~D:Record.Macro.RawHTMLID~}" class="${displayClass(tmpInputType)}"></div>
		</div>
		${tmpLocked ? '' : /*HTML*/`<p class="help"><span class="description-text">{~D:Record.Description~}</span></p>`}`;
}

module.exports = { buildEditorBody, displayClass };
